// Inpsiration taken from https://github.com/mui-org/material-ui/blob/v1-beta/packages/material-ui-icons/src/utils/createSvgIcon.js
import React from 'react'
import PropTypes from 'prop-types'
import MuiSvgIcon from '@material-ui/core/SvgIcon'
import estyled from '@signal/theme/emotion'
import flow from 'lodash/flow'
import camelCase from 'lodash/camelCase'
import upperFirst from 'lodash/upperFirst'
import { createSelector } from 'reselect'
import {
  paletteGreySelector,
  typographySelector
} from '@signal/theme/selectors'
import getSvg from './importSvg'

// Prop selectors
const greyHueSelector = props => props.grey
const sizeSelector = props => props.size
const fillSelector = props => props.fillColor

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SignalSvg = estyled(({ iconColor, fillColor, ...rest }) => (
  <MuiSvgIcon {...rest} />
))`
  ${
    // if the `grey` prop is set, the color of the icon should use that hue of grey
    createSelector(
      greyHueSelector,
      paletteGreySelector,
      (hue, paletteGrey) => paletteGrey[hue] && `color: ${paletteGrey[hue]};`
    )
  }
  ${
    // if the `size` prop is set, the font size of the icon should respect it
    createSelector(
      sizeSelector,
      typographySelector,
      (size, typography) => size && `font-size: ${typography.pxToRem(size)};`
    )
  }
  ${
    // if the `fill` prop is set, the fill of the icon should respect it
    createSelector(
      fillSelector,
      fillColor => fillColor && `.fill { fill: ${fillColor}; }`
    )
  }
`

export const SignalSvgPropTypes = {
  // The hue of grey to make the icon
  grey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.number,
  fillColor: PropTypes.string
}

// use this only for off-center situations in the original svg -  all icons must be sized to 24x24
const ICON_VIEWBOXES = {
  advertise: '-2 -4 24 24',
  infoBubble: '-1 -1 24 24',
  insights: '-0.5 0 24 24',
  inventory: '-1 -1 24 24',
  pushPinNew: '-3.5 -1 24 24'
}

/**
 * Creates an SVG icon React component using the SVG with the provided filename.
 *
 * NOTE: Each SVG should be created with a 24x24 viewport, as it will be
 * rendered with a `viewBox` value of `"0 0 24 24"` unless overridden above
 *
 * @param {String} iconFilename - Filename of an icon inside the `svg` directory
 * if not provided, falls back to the icon filename pascal cased.
 * @return {Component} - SVG icon React component
 */
export function createSvgIcon(iconFilename) {
  const SignalIcon = props => (
    <SignalSvg
      {...props}
      component={getSvg(iconFilename)}
      viewBox={ICON_VIEWBOXES[camelCase(iconFilename)]}
    >
      {iconFilename}
    </SignalSvg>
  )

  // If displayName not provided, fallback to pascal case iconFilename
  SignalIcon.displayName = flow(camelCase, upperFirst)(iconFilename)

  SignalIcon.propTypes = SignalSvgPropTypes

  return SignalIcon
}

export default createSvgIcon
